import React, { useState, useEffect } from 'react';
import { Grid, ButtonBase } from '@material-ui/core';
import { Form, withFormik } from 'formik';
import * as yup from 'yup';
import { Typography, Paper, Button, Wrapper } from '../../ui';
import styled, { css } from 'styled-components';
import { COMPARISON, matchBizType } from './comparisonData';

const OROPOS_LOGO = '/oropos-logo.webp';
const FLOREANT_LOGO =
  'https://floreantpos.org/wp-content/themes/floresuspended/assets/images/floreant-logo-new.webp';

const products = [
  { id: 'both',     content: 'Try both POS systems.',                                              logos: [OROPOS_LOGO, FLOREANT_LOGO], stackLogos: true,  logoWidth: '62%', logoHeight: '46px' },
  { id: 'oropos',   content: 'Full featured POS',  bullets: ['Inventory', 'Floor Plan', 'Loyalty'], logos: [OROPOS_LOGO],                logoWidth: '80%', logoHeight: '70px' },
  { id: 'floreant', content: 'Basic Open source Point of sale, lifetime free with self support.',   logos: [FLOREANT_LOGO],              logoWidth: '92%', logoHeight: '80px' },
];

/* ── product card styles ── */
const ProductTypeWrapper = styled(({ isActive, ...rest }) => <ButtonBase {...rest} />)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 20px 16px;
  justify-content: flex-start;
  transition: all 0.2s ease-in;
  ${({ isActive }) => isActive && css`box-shadow: #3498db 0px 0px 10px;`}
  :hover { box-shadow: #3498db 0px 0px 10px; }
`;

const LogoWrapper = styled.div`
  display: flex;
  flex-direction: ${({ stack }) => (stack ? 'column' : 'row')};
  align-items: center;
  justify-content: center;
  gap: ${({ stack }) => (stack ? '2px' : '12px')};
  margin-bottom: ${({ stack }) => (stack ? '12px' : '14px')};
  width: 100%;
`;

const ProductLogo = styled.img`
  width: ${({ w }) => w || '80%'};
  height: ${({ h }) => h || '70px'};
  object-fit: contain;
`;

const BulletList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 10px 0 0;
  width: 100%;
  text-align: left;
  li {
    font-size: 14px;
    margin-bottom: 6px;
    display: flex;
    align-items: center;
    gap: 8px;
    &::before {
      content: '';
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #3498db;
      flex-shrink: 0;
    }
  }
`;

const CardWrapper = styled.div`
  position: relative;
  height: 100%;
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
  padding: 3px 14px;
  border-radius: 12px;
  background: #ffb800;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  white-space: nowrap;
`;

const CheckBadge = styled.div`
  position: absolute;
  top: -10px;
  right: -10px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #27ae60;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(39,174,96,0.5);
  z-index: 1;
  animation: popIn 0.2s ease-out;
  @keyframes popIn {
    from { transform: scale(0.5); opacity: 0; }
    to   { transform: scale(1);   opacity: 1; }
  }
  &::after {
    content: '';
    width: 9px;
    height: 5px;
    border-left: 2px solid #fff;
    border-bottom: 2px solid #fff;
    transform: rotate(-45deg) translate(1px, -1px);
  }
`;

/* ── comparison panel styles ── */
const CompareToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 28px;
  background: none;
  border: 2px solid #3498db;
  border-radius: 24px;
  color: #3498db;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  letter-spacing: 0.4px;
  transition: all 0.2s ease;
  &:hover { background: #3498db; color: #fff; }
`;

const PluginButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 28px;
  background: #ffb800;
  border: 2px solid #ffb800;
  border-radius: 24px;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-decoration: none;
  box-shadow: 0 2px 8px rgba(255, 184, 0, 0.35);
  transition: all 0.2s ease;
  &:hover { background: #e6a600; border-color: #e6a600; color: #fff; transform: translateY(-1px); }
`;

const CompareActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 12px;
`;

const ChevronSvg = styled.svg`
  transition: transform 0.25s ease;
  ${({ open }) => open && css`transform: rotate(180deg);`}
`;

const ComparePanel = styled.div`
  margin-top: 20px;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.07);
  background: #fff;
`;

const BizTypeRow = styled.div`
  display: flex;
  gap: 8px;
  padding: 14px 18px;
  background: #fff;
  border-bottom: 1px solid #eeeeee;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const BizPill = styled.button`
  flex-shrink: 0;
  padding: 5px 15px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  border: 2px solid ${({ active }) => (active ? '#3498db' : '#e0e0e0')};
  background: ${({ active }) => (active ? '#3498db' : '#fff')};
  color: ${({ active }) => (active ? '#fff' : '#757575')};
  &:hover { border-color: #3498db; color: ${({ active }) => (active ? '#fff' : '#3498db')}; }
`;

const FeatureTabRow = styled.div`
  display: flex;
  padding: 0 18px;
  background: #fff;
  border-bottom: 1px solid #eeeeee;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const FeatureTabBtn = styled.button`
  flex-shrink: 0;
  padding: 11px 14px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  border: none;
  background: none;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ active }) => (active ? '#3498db' : '#9e9e9e')};
  border-bottom: 3px solid ${({ active }) => (active ? '#3498db' : 'transparent')};
  margin-bottom: -1px;
  transition: all 0.15s ease;
  &:hover { color: #3498db; }
`;

const TabSubtitle = styled.div`
  padding: 9px 22px;
  font-size: 12px;
  color: rgba(0,0,0,0.45);
  background: #fff;
  border-bottom: 1px solid #eeeeee;
  font-style: italic;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const ColName = styled.div`
  font-size: 15px;
  font-weight: 800;
  color: rgba(0,0,0,0.87);
  margin-bottom: 5px;
`;

const ColPos = styled.div`
  font-size: 11px;
  color: rgba(0,0,0,0.45);
  line-height: 1.5;
  font-weight: 400;
  max-width: 160px;
  margin: 0 auto;
`;

const CompTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 520px;

  thead tr th {
    padding: 18px 16px 14px;
    background: #fff;
    text-align: center;
    vertical-align: top;
    border-bottom: 1px solid #eeeeee;

    &:first-child {
      border-top: 3px solid #eeeeee;
      text-align: left;
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.8px;
      color: rgba(0,0,0,0.35);
      vertical-align: bottom;
      padding-bottom: 12px;
      width: 50%;
    }
    &:nth-child(2) {
      border-top: 3px solid #3498db;
      background: rgba(52,152,219,0.03);
      min-width: 130px;
    }
    &:nth-child(3) {
      border-top: 3px solid #e0e0e0;
      min-width: 130px;
    }
  }

  tbody tr {
    transition: background 0.1s;
    &:hover td { background: #fafafa !important; }
    &:last-child td { border-bottom: none; }
  }

  tbody tr td {
    padding: 12px 16px;
    border-bottom: 1px solid #f5f5f5;
    vertical-align: middle;
    &:nth-child(2) { text-align: center; background: rgba(52,152,219,0.025); }
    &:nth-child(3) { text-align: center; }
  }
`;

const NeedText = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: rgba(0,0,0,0.87);
  margin-bottom: 2px;
`;

const WhyText = styled.div`
  font-size: 11px;
  color: rgba(0,0,0,0.45);
  line-height: 1.4;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-size: 13px;
  font-weight: 700;
  border: ${({ level }) =>
    level === 'full'    ? '2.5px solid #3498db' :
    level === 'partial' ? '2.5px solid #ffb800' :
    'none'};
  background: transparent;
  color: ${({ level }) =>
    level === 'full'    ? '#3498db' :
    level === 'partial' ? '#ffb800' :
    '#bdbdbd'};
`;

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 11px 22px;
  background: #fff;
  border-top: 1px solid #eeeeee;
  flex-wrap: wrap;
`;

const LegendLabel = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: rgba(0,0,0,0.35);
  margin-right: 6px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  color: rgba(0,0,0,0.54);
`;

/* ── component ── */
const ProductType = ({ formData = {}, section = {}, handleFormData, handleBack, handleNext }) => {
  const [showComp, setShowComp] = useState(false);

  const initialBT = matchBizType(formData.businessType);
  const [activeBT, setActiveBT] = useState(initialBT);
  const [activeTab, setActiveTab] = useState(initialBT.defaultTab);

  useEffect(() => {
    const bt = matchBizType(formData.businessType);
    setActiveBT(bt);
    setActiveTab(bt.defaultTab);
  }, [formData.businessType]);

  const handleBTClick = (bt) => {
    setActiveBT(bt);
    setActiveTab(bt.defaultTab);
  };

  const tabData = COMPARISON.tabs[activeTab];
  const legend = COMPARISON.legend;

  return (
    <Wrapper mx="auto" width={{ xs: '100%', md: '80%' }}>
      <Form style={{ marginBottom: 80 }}>
        <Typography
          variant="body1"
          align="center"
          style={{ fontWeight: 700, fontSize: 18 }}
        >
          {section.subtitles && section.subtitles[0]}
        </Typography>
        <Typography
          variant="body1"
          align="center"
          mb={4}
          style={{ fontSize: 13, color: '#777' }}
        >
          Most popular: ORO POS
        </Typography>

        {/* product cards */}
        <Grid container spacing={8} justify="center">
          {products.map((productType) => {
            const isActive = formData.product === productType.id;
            return (
              <Grid item xs={12} md={4} key={productType.id}>
                <CardWrapper>
                  {productType.id === 'oropos' && <PopularBadge>Most Popular</PopularBadge>}
                  {isActive && <CheckBadge />}
                  <Paper style={{ height: '100%' }}>
                    <ProductTypeWrapper
                      isActive={isActive}
                      onClick={() => handleFormData({ product: productType.id })}
                    >
                      <LogoWrapper stack={productType.stackLogos}>
                        {productType.logos.map((logo, i) => (
                          <ProductLogo key={i} src={logo} alt={productType.id} w={productType.logoWidth} h={productType.logoHeight} />
                        ))}
                      </LogoWrapper>
                      <Typography variant="body1" mt={2} align="center">{productType.content}</Typography>
                      {productType.bullets && (
                        <BulletList>
                          {productType.bullets.map(b => <li key={b}>{b}</li>)}
                        </BulletList>
                      )}
                    </ProductTypeWrapper>
                  </Paper>
                </CardWrapper>
              </Grid>
            );
          })}
        </Grid>

        {/* compare toggle */}
        <Wrapper textAlign="center" mt={6} mb={showComp ? 0 : 4}>
          <CompareActions>
          <CompareToggle onClick={() => setShowComp(!showComp)}>
            {showComp ? 'Hide Comparison' : 'Compare Features'}
            <ChevronSvg open={showComp} width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </ChevronSvg>
          </CompareToggle>
          <PluginButton href="https://shop.orocube.com/cardquote" target="_blank" rel="noopener noreferrer">
            Free Card Processing Plugin
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2 6H10M10 6L6.5 2.5M10 6L6.5 9.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </PluginButton>
          </CompareActions>
        </Wrapper>

        {/* comparison panel */}
        {showComp && (
          <ComparePanel>
            {/* business type pills */}
            <BizTypeRow>
              {COMPARISON.businessTypes.map(bt => (
                <BizPill key={bt.id} active={activeBT.id === bt.id} onClick={() => handleBTClick(bt)}>
                  {bt.name}
                </BizPill>
              ))}
            </BizTypeRow>

            {/* feature sub-tabs */}
            <FeatureTabRow>
              {activeBT.tabs.map(tabKey => (
                <FeatureTabBtn
                  key={tabKey}
                  active={activeTab === tabKey}
                  onClick={() => setActiveTab(tabKey)}
                >
                  {COMPARISON.tabs[tabKey]?.title}
                </FeatureTabBtn>
              ))}
            </FeatureTabRow>

            {tabData?.subtitle && <TabSubtitle>{tabData.subtitle}</TabSubtitle>}

            {/* feature table */}
            <TableWrapper>
              <CompTable>
                <thead>
                  <tr>
                    <th>Features</th>
                    <th>
                      <ColName>{COMPARISON.products.oroPos.name}</ColName>
                      <ColPos>{COMPARISON.products.oroPos.positioning}</ColPos>
                    </th>
                    <th>
                      <ColName>{COMPARISON.products.floreantPos.name}</ColName>
                      <ColPos>{COMPARISON.products.floreantPos.positioning}</ColPos>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tabData?.features.map((f, i) => (
                    <tr key={i}>
                      <td>
                        <NeedText>{f.need}</NeedText>
                        <WhyText>{f.whyItMatters}</WhyText>
                      </td>
                      <td><Badge level={f.oroPos}>{legend[f.oroPos]?.label}</Badge></td>
                      <td><Badge level={f.floreantPos}>{legend[f.floreantPos]?.label}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </CompTable>
            </TableWrapper>

            {/* legend */}
            <LegendRow>
              <LegendLabel>Legend</LegendLabel>
              {Object.entries(legend).map(([key, val]) => (
                <LegendItem key={key}>
                  <Badge level={key}>{val.label}</Badge>
                  {val.text}
                </LegendItem>
              ))}
            </LegendRow>
          </ComparePanel>
        )}

        {/* nav buttons */}
        <Wrapper display="flex" justifyContent="space-between" mt={6}>
          <Button variant="outlined" onClick={handleBack}>Back</Button>
          <Button variant="contained" color="secondary" onClick={handleNext}>Next</Button>
        </Wrapper>
      </Form>
    </Wrapper>
  );
};

const ProductTypeForm = withFormik({
  mapPropsToValues: ({ formData: { productType } }) => ({
    productType: productType || '',
  }),
  validationSchema: yup.object().shape({
    productType: yup.number().required('Please enter your Product Type'),
  }),
})(ProductType);

export default ProductTypeForm;
